/**
 * Cookie consent persistence in localStorage.
 * Structure matches GDPR documentation requirements (timestamp, version).
 */

import {
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  type CookieConsent,
  DEFAULT_CONSENT,
} from './types';

function now(): string {
  return new Date().toISOString();
}

export function getConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(consent: Partial<CookieConsent>): CookieConsent {
  const full: CookieConsent = {
    necessary: true,
    analytics: consent.analytics ?? DEFAULT_CONSENT.analytics,
    functional: consent.functional ?? DEFAULT_CONSENT.functional,
    marketing: consent.marketing ?? DEFAULT_CONSENT.marketing,
    timestamp: consent.timestamp ?? now(),
    version: consent.version ?? CONSENT_VERSION,
  };
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(full));
    } catch (e) {
      console.warn('[CookieConsent] localStorage setItem failed', e);
    }
  }
  return full;
}

/** True if the user has made a consent choice (banner was accepted/rejected/customised). */
export function hasUserConsented(): boolean {
  const c = getConsent();
  return c != null && c.timestamp !== '';
}
